import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { SealCheck, ArrowUpRight, MapPin, CaretRight } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { CATEGORY_META } from "@/data/categories";
import { priceLabel } from "@/lib/format";
import { ProviderAvatar } from "@/components/AvatarUpload";
import { RatingBadge } from "@/components/RatingStars";
import { FoundingBadge } from "@/components/FoundingBadge";

export const VerifiedSlider = ({ city, country, category, endpoint = "/listings/verified", title, subtitle, testId = "verified-slider" }) => {
  const { t, tc } = useLang();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [i, setI] = useState(0);
  const [dir, setDir] = useState(1);
  const dragged = useRef(false);

  useEffect(() => {
    const params = {};
    if (city && city !== "all") params.city = city;
    if (country && country !== "all") params.country = country;
    if (category && category !== "all") params.category = category;
    api.get(endpoint, { params })
      .then(({ data }) => { setItems(data.slice(0, 10)); setI(0); })
      .catch(() => setItems([]));
  }, [city, country, category, endpoint]);

  if (!items.length) return null;

  const l = items[i % items.length];
  const meta = CATEGORY_META[l.category] || {};
  const Icon = meta.icon;
  const price = priceLabel(l, t);

  const next = () => {
    setDir(1);
    setI((n) => (n + 1) % items.length);
  };

  return (
    <section className="mt-5" data-testid={testId}>
      <div className="flex items-baseline justify-between">
        <h2 className="text-base text-ppc-text md:text-lg">{title || t("slider_title")}</h2>
        <span className="text-[12px] text-ppc-muted" data-testid={`${testId}-count`}>
          {i + 1}/{items.length}
        </span>
      </div>
      <p className="mt-1 text-[12.5px] text-ppc-muted">
        {subtitle || (city && city !== "all" ? city : t("slider_local"))}
      </p>

      <div className="relative mt-3 h-[300px] select-none overflow-hidden rounded-3xl">
        <AnimatePresence initial={false} custom={dir}>
          <motion.div
            key={l.id}
            custom={dir}
            initial={{ x: dir > 0 ? "100%" : "-100%", opacity: 0.6 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: dir > 0 ? "-100%" : "100%", opacity: 0.4 }}
            transition={{ type: "spring", stiffness: 320, damping: 34 }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.18}
            onDragStart={() => { dragged.current = false; }}
            onDrag={(e, info) => { if (Math.abs(info.offset.x) > 8) dragged.current = true; }}
            onDragEnd={(e, info) => {
              if (info.offset.x < -50) next();
              else if (info.offset.x > 50) {
                setDir(-1);
                setI((n) => (n - 1 + items.length) % items.length);
              }
            }}
            onClick={() => {
              if (dragged.current) { dragged.current = false; return; }
              navigate(`/listing/${l.id}`);
            }}
            data-testid={`${testId}-slide-${l.id}`}
            className="svc-card absolute inset-0 h-full cursor-pointer"
          >
            <img src={l.image || meta.image} alt={tc(l.category)} className="svc-card-img" />
            <span className="svc-card-veil" aria-hidden="true" />
            <div className="relative flex h-full flex-col justify-between p-4">
              <div className="flex items-start justify-between gap-2">
                <span className="glass-pill flex items-center gap-2">
                  <ProviderAvatar
                    name={l.provider_name}
                    src={l.provider_avatar ? `${process.env.REACT_APP_BACKEND_URL}${l.provider_avatar}` : null}
                    size={26}
                  />
                  <span className="truncate text-[12.5px] font-medium text-ppc-text">{l.provider_name}</span>
                  <SealCheck size={14} weight="fill" color="#9184d9" />
                </span>
                <span className="glass-pill shrink-0 text-[13px] font-medium text-ppc-text">
                  {price.main}
                  <span className="ml-0.5 text-[10.5px] text-ppc-muted">{price.unit}</span>
                </span>
              </div>

              <div>
                {l.founding_member && <FoundingBadge fm={l.founding_member} className="mb-2" />}
                <span className="glass-pill inline-flex items-center gap-1.5 text-[12px] text-ppc-text">
                  {Icon && <Icon className="h-3.5 w-3.5" style={{ color: "#c6bff0" }} />} {tc(l.category)}
                </span>
                <h3 className="mt-2 line-clamp-1 text-[18px] font-medium text-ppc-text">{l.title}</h3>
                <p className="svc-card-desc mt-1 line-clamp-2">{l.description}</p>
                <div className="mt-3 flex items-center justify-between gap-2">
                  <span className="glass-pill flex min-w-0 items-center gap-1.5 text-[12px] text-ppc-text">
                    <MapPin size={13} weight="fill" color="#c6bff0" />
                    <span className="truncate">{l.city}, {l.country}</span>
                  </span>
                  <button
                    type="button"
                    data-testid={`verified-slide-open-${l.id}`}
                    onClick={(ev) => {
                      ev.stopPropagation();
                      navigate(`/listing/${l.id}`);
                    }}
                    className="glass-pill flex shrink-0 items-center gap-1.5 text-[12px] text-primary-green"
                  >
                    {t("hero_cta")} <ArrowUpRight size={13} weight="bold" />
                  </button>
                </div>
                {l.rating_count > 0 && <RatingBadge avg={l.rating} count={l.rating_count} className="mt-2" />}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {items.length > 1 && (
          <button
            type="button"
            onClick={next}
            data-testid="verified-slider-next"
            className="glass-pill absolute right-3 top-1/2 z-10 grid h-9 w-9 -translate-y-1/2 place-items-center !rounded-full !p-0"            aria-label="next"
          >
            <CaretRight size={16} color="#e9e9ed" />
          </button>
        )}
      </div>

      <div className="mt-2.5 flex justify-center gap-1.5">
        {items.map((it, idx) => (
          <span
            key={it.id}
            className="h-1.5 rounded-full transition-all"
            style={{
              width: idx === i ? 18 : 6,
              background: idx === i ? "#9184d9" : "rgba(233,233,237,0.25)",
            }}
          />
        ))}
      </div>
    </section>
  );
};
